import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase.js";
import { toast } from "react-toastify";

const MEETING_TYPE_LABELS = {
  annual: "Annual member meeting",
  regular_board: "Board meeting",
  special: "Special meeting",
  budget: "Budget meeting",
  executive_session: "Executive session",
};

const ELECTION_STATUS_LABELS = {
  planning: "Planning",
  nominations_open: "Nominations open",
  voting: "Voting in progress",
};

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function ComplianceCalendarPage({ isMobile }) {
  const [loading, setLoading] = useState(true);
  const [disclaimer, setDisclaimer] = useState("");
  const [meetings, setMeetings] = useState([]);
  const [elections, setElections] = useState([]);

  const loadCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const fn = httpsCallable(functions, "getPublicComplianceCalendar");
      const { data } = await fn();
      setMeetings(data.meetings || []);
      setElections(data.elections || []);
      setDisclaimer(data.disclaimer || "");
    } catch (error) {
      toast.error(`Failed to load governance calendar: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = "Governance Calendar | Nantahala Mountain Resort";
    return () => {
      document.title = "NMR Community";
    };
  }, []);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  return (
    <div className={`compliance-calendar-page ${isMobile ? "compliance-calendar-page--mobile" : ""}`}>
      <style>{`
        .compliance-calendar-page { padding: ${isMobile ? "1rem" : "2rem"}; max-width: 900px; margin: 0 auto; }
        .compliance-calendar-title { font-size: ${isMobile ? "1.35rem" : "1.6rem"}; color: #1a3c34; margin: 0 0 0.5rem; }
        .compliance-calendar-lead { color: #4b5563; line-height: 1.5; margin: 0 0 1rem; }
        .compliance-calendar-disclaimer { font-size: 0.85rem; background: #fffbeb; border: 1px solid #fcd34d; color: #92400e; padding: 0.65rem 0.85rem; border-radius: 8px; margin-bottom: 1rem; }
        .compliance-calendar-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 1rem; margin-bottom: 0.75rem; background: #fff; }
        .compliance-calendar-card h3 { margin: 0 0 0.35rem; font-size: 1.05rem; color: #0f172a; }
        .compliance-calendar-meta { font-size: 0.9rem; color: #475569; margin: 0.15rem 0; }
        .compliance-calendar-badge { display: inline-block; font-size: 0.75rem; font-weight: 600; padding: 0.15rem 0.5rem; border-radius: 999px; background: #dbeafe; color: #1e40af; margin-left: 0.35rem; }
        .compliance-calendar-empty { text-align: center; padding: 2rem 1rem; color: #64748b; border: 1px dashed #cbd5e1; border-radius: 12px; }
        .compliance-calendar-links { margin-top: 1.25rem; font-size: 0.9rem; }
      `}</style>

      <h1 className="compliance-calendar-title">Governance Calendar</h1>
      <p className="compliance-calendar-lead">
        Upcoming HOA meetings open to owners and active board elections. For meeting minutes and board contacts, see{" "}
        <Link to="/meeting-minutes">Meeting Minutes</Link> and <Link to="/board-members">Board Members</Link>.
      </p>
      {disclaimer ? <p className="compliance-calendar-disclaimer">{disclaimer}</p> : null}

      {loading ? (
        <p>Loading calendar…</p>
      ) : (
        <>
          <h2>Upcoming meetings</h2>
          {meetings.length === 0 ? (
            <div className="compliance-calendar-empty">No owner-visible meetings are scheduled right now.</div>
          ) : (
            meetings.map((m) => (
              <article key={m.id} className="compliance-calendar-card">
                <h3>
                  {m.title}
                  <span className="compliance-calendar-badge">{MEETING_TYPE_LABELS[m.type] || m.type}</span>
                </h3>
                <p className="compliance-calendar-meta">{formatDate(m.scheduledAt)}</p>
                {m.location ? <p className="compliance-calendar-meta">{m.location}</p> : null}
                {m.agendaUrl ? (
                  <p className="compliance-calendar-meta">
                    <a href={m.agendaUrl} target="_blank" rel="noopener noreferrer">
                      View agenda
                    </a>
                  </p>
                ) : null}
              </article>
            ))
          )}

          <h2 style={{ marginTop: "1.5rem" }}>Elections</h2>
          {elections.length === 0 ? (
            <div className="compliance-calendar-empty">No active elections at this time.</div>
          ) : (
            elections.map((e) => (
              <article key={e.id} className="compliance-calendar-card">
                <h3>{e.title}</h3>
                <p className="compliance-calendar-meta">
                  Election: {formatDate(e.electionDate)}
                  <span className="compliance-calendar-badge">
                    {ELECTION_STATUS_LABELS[e.status] || e.status}
                  </span>
                </p>
                {e.nominationDeadline ? (
                  <p className="compliance-calendar-meta">Nominations due: {formatDate(e.nominationDeadline)}</p>
                ) : null}
                {e.positions?.length ? (
                  <p className="compliance-calendar-meta">
                    Open seats: {e.positions.map((p) => p.title).join(", ")}
                  </p>
                ) : null}
                {e.ballotMethod === "poll" ? (
                  <p className="compliance-calendar-meta">
                    <Link to="/polls">Vote in community polls</Link> when the ballot opens.
                  </p>
                ) : null}
              </article>
            ))
          )}

          <p className="compliance-calendar-links">
            Questions about notice requirements or eligibility? Contact the board via{" "}
            <Link to="/board-members">Board Members</Link>.
          </p>
        </>
      )}
    </div>
  );
}

export default ComplianceCalendarPage;